import React, { useEffect, useState } from "react";
import { getMaterialLabel, TOOL_COATINGS } from "../../data/machiningMaterials";
import MaterialSelector from "../MaterialSelector";
import ToolCoatingSelector from "../ToolCoatingSelector";

const MATERIAL_LABELS = {
  aluminum_6061: "Aluminum 6061",
  aluminum_7075: "Aluminum 7075",
  mild_steel: "Mild Steel",
  cold_rolled_steel: "Cold Rolled Steel",
  hot_rolled_steel: "Hot Rolled Steel",
  alloy_steel: "Alloy Steel",
  stainless_304: "Stainless 304",
  stainless_316: "Stainless 316",
  tool_steel: "Tool Steel",
  cast_iron: "Cast Iron",
  brass: "Brass",
  bronze: "Bronze",
  titanium: "Titanium",
  inconel: "Inconel",
};

const MATERIAL_PROFILES = {
  aluminum_6061: {
    label: "Aluminum 6061",
    sfmLow: 400,
    sfmHigh: 1000,
    chipLow: 0.0015,
    chipHigh: 0.006,
  },
  aluminum_7075: {
    label: "Aluminum 7075",
    sfmLow: 350,
    sfmHigh: 900,
    chipLow: 0.0015,
    chipHigh: 0.006,
  },
  mild_steel: {
    label: "Mild Steel",
    sfmLow: 150,
    sfmHigh: 450,
    chipLow: 0.001,
    chipHigh: 0.004,
  },
  cold_rolled_steel: {
    label: "Cold Rolled Steel",
    sfmLow: 150,
    sfmHigh: 450,
    chipLow: 0.001,
    chipHigh: 0.004,
  },
  hot_rolled_steel: {
    label: "Hot Rolled Steel",
    sfmLow: 125,
    sfmHigh: 400,
    chipLow: 0.0008,
    chipHigh: 0.0035,
  },
  alloy_steel: {
    label: "Alloy Steel",
    sfmLow: 100,
    sfmHigh: 350,
    chipLow: 0.0008,
    chipHigh: 0.0035,
  },
  stainless_304: {
    label: "Stainless 304",
    sfmLow: 80,
    sfmHigh: 250,
    chipLow: 0.0008,
    chipHigh: 0.003,
  },
  stainless_316: {
    label: "Stainless 316",
    sfmLow: 70,
    sfmHigh: 220,
    chipLow: 0.0008,
    chipHigh: 0.003,
  },
  tool_steel: {
    label: "Tool Steel",
    sfmLow: 60,
    sfmHigh: 220,
    chipLow: 0.0006,
    chipHigh: 0.0028,
  },
  cast_iron: {
    label: "Cast Iron",
    sfmLow: 120,
    sfmHigh: 400,
    chipLow: 0.001,
    chipHigh: 0.0045,
  },
  brass: {
    label: "Brass",
    sfmLow: 250,
    sfmHigh: 700,
    chipLow: 0.001,
    chipHigh: 0.005,
  },
  bronze: {
    label: "Bronze",
    sfmLow: 180,
    sfmHigh: 550,
    chipLow: 0.001,
    chipHigh: 0.0045,
  },
  titanium: {
    label: "Titanium",
    sfmLow: 50,
    sfmHigh: 180,
    chipLow: 0.0006,
    chipHigh: 0.0025,
  },
  inconel: {
    label: "Inconel",
    sfmLow: 30,
    sfmHigh: 120,
    chipLow: 0.0004,
    chipHigh: 0.002,
  },
};

const TOOL_MATERIAL_MULTIPLIERS = {
  hss: {
    label: "HSS",
    lifeMultiplier: 0.75,
    confidencePenalty: true,
  },
  cobalt: {
    label: "Cobalt",
    lifeMultiplier: 0.9,
    confidencePenalty: false,
  },
  carbide: {
    label: "Carbide",
    lifeMultiplier: 1,
    confidencePenalty: false,
  },
  insert: {
    label: "Indexable Insert",
    lifeMultiplier: 1.1,
    confidencePenalty: false,
  },
  custom: {
    label: "Custom / Unknown",
    lifeMultiplier: 0.85,
    confidencePenalty: true,
  },
};

const DEFAULT_PARAMS = {
  material: "mild_steel",
  toolMaterial: "carbide",
  toolCoating: "uncoated",
  toolDiameter: 0.5,
  flutes: 4,
  spindleRPM: 2500,
  feedRate: 20,
  cutterCost: 35,
  toolLifeValue: 60,
  toolLifeMode: "minutes",
  cutTime: "",
  cutLength: "",
  coolantType: "Flood",
};

const formatMoney = (value) => {
  if (!Number.isFinite(value)) return "$0.00";

  return value.toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  });
};

const formatNumber = (value, digits = 4) => {
  if (!Number.isFinite(value)) return "0";

  return value.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: digits,
  });
};

const calculateToolCostPerInch = (params) => {
  const errors = {};

  const materialProfile =
    MATERIAL_PROFILES[params.material] || MATERIAL_PROFILES.mild_steel;

  const toolMaterialProfile =
    TOOL_MATERIAL_MULTIPLIERS[params.toolMaterial] ||
    TOOL_MATERIAL_MULTIPLIERS.custom;
  const toolCoatingProfile = TOOL_COATINGS[params.toolCoating] || TOOL_COATINGS.uncoated;

  const toolDiameter = Number(params.toolDiameter);
  const flutes = Number(params.flutes);
  const spindleRPM = Number(params.spindleRPM);
  const feedRate = Number(params.feedRate);
  const cutterCost = Number(params.cutterCost);
  const toolLifeValue = Number(params.toolLifeValue);
  const cutTime = params.cutTime === "" ? null : Number(params.cutTime);
  const cutLength = params.cutLength === "" ? null : Number(params.cutLength);

  if (!params.material) {
    errors.material = "Material is required.";
  }

  if (!params.toolMaterial) {
    errors.toolMaterial = "Tool material is required.";
  }
  if (!params.toolCoating) {
    errors.toolCoating = "Tool coating is required.";
  }

  if (!Number.isFinite(toolDiameter) || toolDiameter <= 0) {
    errors.toolDiameter = "Enter a valid tool diameter.";
  }

  if (!Number.isFinite(flutes) || flutes <= 0) {
    errors.flutes = "Enter a valid flute count.";
  }

  if (!Number.isFinite(spindleRPM) || spindleRPM <= 0) {
    errors.spindleRPM = "Enter a valid RPM.";
  }

  if (!Number.isFinite(feedRate) || feedRate <= 0) {
    errors.feedRate = "Enter a valid feed rate.";
  }

  if (!Number.isFinite(cutterCost) || cutterCost <= 0) {
    errors.cutterCost = "Enter a valid cutter cost.";
  }

  if (!Number.isFinite(toolLifeValue) || toolLifeValue <= 0) {
    errors.toolLifeValue = "Enter a valid tool life.";
  }

  if (params.cutTime !== "" && (!Number.isFinite(cutTime) || cutTime < 0)) {
    errors.cutTime = "Enter a valid cut time.";
  }

  if (
    params.cutLength !== "" &&
    (!Number.isFinite(cutLength) || cutLength < 0)
  ) {
    errors.cutLength = "Enter a valid cut length.";
  }

  if (Object.keys(errors).length > 0) {
    return { errors };
  }

  const materialLabel =
    getMaterialLabel(params.material) || MATERIAL_LABELS[params.material] || params.material.replace(/_/g, " ");

  const surfaceSpeed = (Math.PI * toolDiameter * spindleRPM) / 12;
  const chipLoad = feedRate / (spindleRPM * flutes);
  const adjustedToolLife =
    toolLifeValue *
    toolMaterialProfile.lifeMultiplier *
    toolCoatingProfile.wearResistanceMultiplier;

  const lifeInMinutes =
    params.toolLifeMode === "minutes"
      ? adjustedToolLife
      : adjustedToolLife / feedRate;

  const lifeInInches =
    params.toolLifeMode === "inches"
      ? adjustedToolLife
      : adjustedToolLife * feedRate;

  const costPerMinute = cutterCost / lifeInMinutes;
  const costPerInch = cutterCost / lifeInInches;

  const estimatedCutLength =
    Number.isFinite(cutLength) && cutLength > 0
      ? cutLength
      : Number.isFinite(cutTime) && cutTime > 0
      ? feedRate * cutTime
      : null;

  const estimatedCutTime =
    Number.isFinite(cutTime) && cutTime > 0
      ? cutTime
      : Number.isFinite(cutLength) && cutLength > 0
      ? cutLength / feedRate
      : null;

  const estimatedToolCost =
    estimatedCutLength !== null ? estimatedCutLength * costPerInch : null;

  const percentOfToolLifeUsed =
    estimatedCutLength !== null
      ? (estimatedCutLength / lifeInInches) * 100
      : null;

  let efficiencyRating = "High";
  let confidence = "High";

  if (costPerInch > 0.25 || lifeInMinutes < 20) {
    efficiencyRating = "Low";
  } else if (costPerInch > 0.08 || lifeInMinutes < 60) {
    efficiencyRating = "Medium";
  }

  if (toolMaterialProfile.confidencePenalty) {
    confidence = "Medium";
  }

  if (
    surfaceSpeed < materialProfile.sfmLow * 0.5 ||
    surfaceSpeed > materialProfile.sfmHigh * 1.5 ||
    chipLoad < materialProfile.chipLow * 0.5 ||
    chipLoad > materialProfile.chipHigh * 1.5 ||
    (percentOfToolLifeUsed !== null && percentOfToolLifeUsed > 100)
  ) {
    confidence = "Low";
  }

  const warnings = [];

  if (surfaceSpeed < materialProfile.sfmLow) {
    warnings.push(
      `Surface speed is below the typical range for ${materialLabel}. The tool may rub instead of cutting efficiently.`
    );
  }

  if (surfaceSpeed > materialProfile.sfmHigh) {
    warnings.push(
      `Surface speed is above the typical range for ${materialLabel}. Tool wear and heat may increase.`
    );
  }

  if (chipLoad < materialProfile.chipLow) {
    warnings.push(
      "Chip load is low. This can cause rubbing, heat buildup, poor finish, and shortened tool life."
    );
  }

  if (chipLoad > materialProfile.chipHigh) {
    warnings.push(
      "Chip load is high. Watch for chatter, tool deflection, poor finish, or tool breakage."
    );
  }

  if (costPerInch > 0.25) {
    warnings.push(
      "Tooling cost per inch is high. Review toolpath load, insert selection, coating, rigidity, coolant, or feed strategy."
    );
  }

  if (lifeInMinutes < 20) {
    warnings.push(
      "Expected tool life is short. Check speed, feed, chip evacuation, coolant, material hardness, and setup rigidity."
    );
  }

  if (percentOfToolLifeUsed !== null && percentOfToolLifeUsed > 100) {
    warnings.push(
      "Estimated cut exceeds expected tool life. Plan for a tool change before the operation is complete."
    );
  }

  if (params.toolLifeMode === "minutes") {
    warnings.push(
      "Tool life entered in minutes depends on feed rate. If feed changes, cost per inch changes too."
    );
  }

  if (
    params.coolantType === "Dry" &&
    surfaceSpeed > materialProfile.sfmHigh * 0.85
  ) {
    warnings.push(
      "Dry cutting near the upper surface-speed range may shorten tool life. Consider flood coolant, MQL, or reducing RPM."
    );
  }

  if (warnings.length === 0) {
    warnings.push("No major tooling cost or cutting condition warnings detected.");
  }

  return {
    materialLabel,
    toolMaterialLabel: toolMaterialProfile.label,
    toolCoatingLabel: toolCoatingProfile.label,
    surfaceSpeed: Number(surfaceSpeed.toFixed(1)),
    chipLoad: Number(chipLoad.toFixed(5)),
    costPerInch,
    costPerMinute,
    lifeInInches,
    lifeInMinutes,
    estimatedCutLength,
    estimatedCutTime,
    estimatedToolCost,
    percentOfToolLifeUsed,
    efficiencyRating,
    confidence,
    warnings,
  };
};

const ToolCostPerInchCalculator = ({
  onSave,
  activeProjectName = "",
  savingCalculation = false,
}) => {
  const [params, setParams] = useState(DEFAULT_PARAMS);
  const [results, setResults] = useState(null);
  const [errors, setErrors] = useState({});
  const [advanced, setAdvanced] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const nextResults = calculateToolCostPerInch(params);

    if (nextResults.errors) {
      setErrors(nextResults.errors);
      setResults(null);
      return;
    }

    setErrors({});
    setResults(nextResults);
  }, [params]);

  const handleInputChange = (field, value) => {
    setParams((currentParams) => ({
      ...currentParams,
      [field]:
        field === "material" ||
        field === "toolMaterial" ||
        field === "toolCoating" ||
        field === "toolLifeMode" ||
        field === "coolantType" ||
        value === ""
          ? value
          : Number(value),
    }));
  };

  const resetForm = () => {
    setParams(DEFAULT_PARAMS);
    setErrors({});
    setResults(null);
    setCopied(false);
  };

  const duplicateCalculation = () => {
    setParams((currentParams) => ({
      ...currentParams,
      spindleRPM: Number(currentParams.spindleRPM) + 100,
    }));
  };

  const buildSummary = () => {
    if (!results) return "Tool Cost Per Inch Cut calculation";

    return [
      "Tool Cost Per Inch Cut",
      `Material: ${results.materialLabel}`,
      `Tool Material: ${results.toolMaterialLabel}`,
      `Tool Coating: ${results.toolCoatingLabel}`,
      `Tool Diameter: ${formatNumber(Number(params.toolDiameter), 4)} in`,
      `Flutes: ${formatNumber(Number(params.flutes), 0)}`,
      `RPM: ${formatNumber(Number(params.spindleRPM), 0)}`,
      `Feed Rate: ${formatNumber(Number(params.feedRate), 3)} IPM`,
      `Surface Speed: ${formatNumber(results.surfaceSpeed, 1)} SFM`,
      `Chip Load: ${formatNumber(results.chipLoad, 5)} IPT`,
      `Cutter Cost: ${formatMoney(Number(params.cutterCost))}`,
      `Tool Life: ${formatNumber(Number(params.toolLifeValue), 2)} ${params.toolLifeMode}`,
      `Adjusted Life: ${formatNumber(results.lifeInMinutes, 2)} min / ${formatNumber(results.lifeInInches, 2)} in`,
      `Cost Per Inch: ${formatMoney(results.costPerInch)}`,
      `Cost Per Minute: ${formatMoney(results.costPerMinute)}`,
      results.estimatedToolCost !== null
        ? `Estimated Tool Cost: ${formatMoney(results.estimatedToolCost)}`
        : null,
      results.estimatedCutLength !== null
        ? `Estimated Cut Length: ${formatNumber(results.estimatedCutLength, 2)} in`
        : null,
      results.estimatedCutTime !== null
        ? `Estimated Cut Time: ${formatNumber(results.estimatedCutTime, 2)} min`
        : null,
      results.percentOfToolLifeUsed !== null
        ? `Tool Life Used: ${formatNumber(results.percentOfToolLifeUsed, 1)}%`
        : null,
      `Efficiency Rating: ${results.efficiencyRating}`,
      `Confidence: ${results.confidence}`,
    ]
      .filter(Boolean)
      .join("\n");
  };

  const copyToClipboard = async () => {
    if (!results) return;

    try {
      await navigator.clipboard.writeText(buildSummary());
      setCopied(true);

      window.setTimeout(() => {
        setCopied(false);
      }, 1800);
    } catch (error) {
      console.error("Failed to copy calculation:", error);
    }
  };

  const exportToCSV = () => {
    if (!results) return;

    const csv = `Parameter,Value
Material,${results.materialLabel}
Tool Material,${results.toolMaterialLabel}
Tool Coating,${results.toolCoatingLabel}
Tool Diameter,${params.toolDiameter}
Flutes,${params.flutes}
RPM,${params.spindleRPM}
Feed Rate,${params.feedRate} IPM
Surface Speed,${results.surfaceSpeed} SFM
Chip Load,${results.chipLoad} IPT
Cutter Cost,${params.cutterCost}
Tool Life,${params.toolLifeValue} ${params.toolLifeMode}
Adjusted Life Minutes,${results.lifeInMinutes}
Adjusted Life Inches,${results.lifeInInches}
Cost Per Inch,${results.costPerInch}
Cost Per Minute,${results.costPerMinute}
Estimated Cut Length,${results.estimatedCutLength || ""}
Estimated Cut Time,${results.estimatedCutTime || ""}
Estimated Tool Cost,${results.estimatedToolCost || ""}
Tool Life Used,${results.percentOfToolLifeUsed || ""}
Efficiency,${results.efficiencyRating}
Confidence,${results.confidence}
Warnings,"${results.warnings.join(" | ")}"`;

    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");

    a.href = url;
    a.download = "tool-cost-per-inch-calculation.csv";
    a.click();

    window.URL.revokeObjectURL(url);
  };

  const handleSave = () => {
    if (!results || !onSave) return;

    onSave({
      summary: `${results.materialLabel} | Ø${params.toolDiameter}" | ${formatMoney(results.costPerInch)} per inch @ ${params.spindleRPM} RPM`,
      input: {
        material: results.materialLabel,
        toolMaterial: results.toolMaterialLabel,
        toolCoating: results.toolCoatingLabel,
        toolDiameter: params.toolDiameter,
        flutes: params.flutes,
        spindleRPM: params.spindleRPM,
        feedRate: params.feedRate,
        cutterCost: params.cutterCost,
        toolLifeValue: params.toolLifeValue,
        toolLifeMode: params.toolLifeMode,
        cutTime: params.cutTime,
        cutLength: params.cutLength,
        coolantType: params.coolantType,
      },
      results: {
        surfaceSpeed: results.surfaceSpeed,
        chipLoad: results.chipLoad,
        costPerInch: results.costPerInch,
        costPerMinute: results.costPerMinute,
        lifeInInches: results.lifeInInches,
        lifeInMinutes: results.lifeInMinutes,
        estimatedCutLength: results.estimatedCutLength,
        estimatedCutTime: results.estimatedCutTime,
        estimatedToolCost: results.estimatedToolCost,
        percentOfToolLifeUsed: results.percentOfToolLifeUsed,
        efficiencyRating: results.efficiencyRating,
      },
      warnings: results.warnings || [],
      confidence: results.confidence || "N/A",
      activeProjectName,
    });
  };

  return (
    <div className="calculator-page">
      <div className="calculator-shell">
        <div className="calculator-header" style={{ marginBottom: "24px" }}>
          <h1 style={{ color: "var(--text-primary)", marginBottom: "8px" }}>
            💲 Tool Cost Per Inch Cut
          </h1>
          <p style={{ color: "var(--text-muted)", margin: 0 }}>
            Calculate tooling cost using material, tool material, cutter size,
            RPM, feed rate, tool life, and operation length.
          </p>
        </div>

        <div className="calculator-layout calculator-layout-wide-results">
          <div className="calculator-panel">
            <div className="form-section" style={{ marginBottom: "24px" }}>
              <h3 style={{ color: "var(--text-primary)", marginBottom: "16px" }}>
                Material & Tool
              </h3>

              <MaterialSelector
                label="Work Material"
                value={params.material}
                onChange={(value) => handleInputChange("material", value)}
                error={errors.material}
              />

              <div className="form-group">
                <label>Tool Material</label>
                <select
                  value={params.toolMaterial}
                  onChange={(e) =>
                    handleInputChange("toolMaterial", e.target.value)
                  }
                  className={errors.toolMaterial ? "error" : ""}
                >
                  {Object.keys(TOOL_MATERIAL_MULTIPLIERS).map((key) => (
                    <option key={key} value={key}>
                      {TOOL_MATERIAL_MULTIPLIERS[key].label}
                    </option>
                  ))}
                </select>
                {errors.toolMaterial && (
                  <span className="error-message">{errors.toolMaterial}</span>
                )}
              </div>

              <ToolCoatingSelector
                value={params.toolCoating}
                onChange={(value) => handleInputChange("toolCoating", value)}
                error={errors.toolCoating}
              />

              <div className="form-group">
                <label>Tool Diameter (inches)</label>
                <input
                  type="number"
                  step="0.001"
                  value={params.toolDiameter}
                  onChange={(e) =>
                    handleInputChange("toolDiameter", e.target.value)
                  }
                  className={errors.toolDiameter ? "error" : ""}
                />
                {errors.toolDiameter && (
                  <span className="error-message">{errors.toolDiameter}</span>
                )}
              </div>

              <div className="form-group">
                <label>Number of Flutes</label>
                <input
                  type="number"
                  min="1"
                  max="16"
                  value={params.flutes}
                  onChange={(e) => handleInputChange("flutes", e.target.value)}
                  className={errors.flutes ? "error" : ""}
                />
                {errors.flutes && (
                  <span className="error-message">{errors.flutes}</span>
                )}
              </div>
            </div>

            <div className="form-section" style={{ marginBottom: "24px" }}>
              <h3 style={{ color: "var(--text-primary)", marginBottom: "16px" }}>
                Cutting Parameters
              </h3>

              <div className="form-group">
                <label>Spindle RPM</label>
                <input
                  type="number"
                  value={params.spindleRPM}
                  onChange={(e) =>
                    handleInputChange("spindleRPM", e.target.value)
                  }
                  className={errors.spindleRPM ? "error" : ""}
                />
                {errors.spindleRPM && (
                  <span className="error-message">{errors.spindleRPM}</span>
                )}
              </div>

              <div className="form-group">
                <label>Feed Rate (IPM)</label>
                <input
                  type="number"
                  step="0.001"
                  value={params.feedRate}
                  onChange={(e) => handleInputChange("feedRate", e.target.value)}
                  className={errors.feedRate ? "error" : ""}
                />
                {errors.feedRate && (
                  <span className="error-message">{errors.feedRate}</span>
                )}
              </div>
            </div>

            <div className="form-section" style={{ marginBottom: "24px" }}>
              <h3 style={{ color: "var(--text-primary)", marginBottom: "16px" }}>
                Cost & Tool Life
              </h3>

              <div className="form-group">
                <label>Cost of Cutter ($)</label>
                <input
                  type="number"
                  step="0.01"
                  value={params.cutterCost}
                  onChange={(e) =>
                    handleInputChange("cutterCost", e.target.value)
                  }
                  className={errors.cutterCost ? "error" : ""}
                />
                {errors.cutterCost && (
                  <span className="error-message">{errors.cutterCost}</span>
                )}
              </div>

              <div className="form-group">
                <label>Tool Life</label>
                <input
                  type="number"
                  step="0.01"
                  value={params.toolLifeValue}
                  onChange={(e) =>
                    handleInputChange("toolLifeValue", e.target.value)
                  }
                  className={errors.toolLifeValue ? "error" : ""}
                />
                {errors.toolLifeValue && (
                  <span className="error-message">{errors.toolLifeValue}</span>
                )}
              </div>

              <div className="form-group">
                <label>Tool Life Unit</label>
                <select
                  value={params.toolLifeMode}
                  onChange={(e) =>
                    handleInputChange("toolLifeMode", e.target.value)
                  }
                >
                  <option value="minutes">Minutes Cut</option>
                  <option value="inches">Inches Cut</option>
                </select>
              </div>
            </div>

            {advanced && (
              <div className="form-section" style={{ marginBottom: "24px" }}>
                <h3 style={{ color: "var(--text-primary)", marginBottom: "16px" }}>
                  Advanced Options
                </h3>

                <div className="form-group">
                  <label>Cut Time Optional (minutes)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={params.cutTime}
                    onChange={(e) => handleInputChange("cutTime", e.target.value)}
                    className={errors.cutTime ? "error" : ""}
                    placeholder="Optional"
                  />
                  {errors.cutTime && (
                    <span className="error-message">{errors.cutTime}</span>
                  )}
                </div>

                <div className="form-group">
                  <label>Cut Length Optional (inches)</label>
                  <input
                    type="number"
                    step="0.001"
                    value={params.cutLength}
                    onChange={(e) =>
                      handleInputChange("cutLength", e.target.value)
                    }
                    className={errors.cutLength ? "error" : ""}
                    placeholder="Optional"
                  />
                  {errors.cutLength && (
                    <span className="error-message">{errors.cutLength}</span>
                  )}
                </div>

                <div className="form-group">
                  <label>Coolant Type</label>
                  <select
                    value={params.coolantType}
                    onChange={(e) =>
                      handleInputChange("coolantType", e.target.value)
                    }
                  >
                    <option>Dry</option>
                    <option>Flood</option>
                    <option>MQL</option>
                  </select>
                </div>
              </div>
            )}

            <div
              className="form-actions"
              style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}
            >
              <button
                onClick={() => setAdvanced(!advanced)}
                className="btn-secondary"
                type="button"
              >
                {advanced ? "Hide" : "Show"} Advanced Options
              </button>
              <button onClick={resetForm} className="btn-secondary" type="button">
                Reset
              </button>
            </div>
          </div>

          <div className="calculator-panel">
            {results ? (
              <>
                <div
                  className="results-header"
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: "12px",
                    alignItems: "center",
                    flexWrap: "wrap",
                    marginBottom: "20px",
                  }}
                >
                  <h3 style={{ color: "var(--text-primary)", margin: 0 }}>
                    Results
                  </h3>

                  <div
                    className="confidence-indicator"
                    style={{ color: "var(--text-secondary)" }}
                  >
                    Confidence:{" "}
                    <span
                      className={`confidence-${results.confidence.toLowerCase()}`}
                      style={{ color: "var(--accent)", fontWeight: 700 }}
                    >
                      {results.confidence}
                    </span>
                  </div>
                </div>

                <div
                  className="results-grid"
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                    gap: "12px",
                    marginBottom: "20px",
                  }}
                >
                  <div
                    className="result-item"
                    style={{
                      background: "var(--bg-surface)",
                      border: "1px solid var(--border-color)",
                      borderRadius: "10px",
                      padding: "14px",
                    }}
                  >
                    <label
                      style={{
                        display: "block",
                        color: "var(--text-muted)",
                        fontSize: "14px",
                        marginBottom: "6px",
                      }}
                    >
                      Cost Per Inch
                    </label>
                    <span
                      style={{
                        color: "var(--text-primary)",
                        fontWeight: 700,
                        fontSize: "18px",
                      }}
                    >
                      {formatMoney(results.costPerInch)}
                    </span>
                  </div>

                  <div
                    className="result-item"
                    style={{
                      background: "var(--bg-surface)",
                      border: "1px solid var(--border-color)",
                      borderRadius: "10px",
                      padding: "14px",
                    }}
                  >
                    <label
                      style={{
                        display: "block",
                        color: "var(--text-muted)",
                        fontSize: "14px",
                        marginBottom: "6px",
                      }}
                    >
                      Cost Per Minute
                    </label>
                    <span
                      style={{
                        color: "var(--text-primary)",
                        fontWeight: 700,
                        fontSize: "18px",
                      }}
                    >
                      {formatMoney(results.costPerMinute)}
                    </span>
                  </div>

                  <div
                    className="result-item"
                    style={{
                      background: "var(--bg-surface)",
                      border: "1px solid var(--border-color)",
                      borderRadius: "10px",
                      padding: "14px",
                    }}
                  >
                    <label
                      style={{
                        display: "block",
                        color: "var(--text-muted)",
                        fontSize: "14px",
                        marginBottom: "6px",
                      }}
                    >
                      Surface Speed
                    </label>
                    <span
                      style={{
                        color: "var(--text-primary)",
                        fontWeight: 700,
                        fontSize: "18px",
                      }}
                    >
                      {formatNumber(results.surfaceSpeed, 1)} SFM
                    </span>
                  </div>

                  <div
                    className="result-item"
                    style={{
                      background: "var(--bg-surface)",
                      border: "1px solid var(--border-color)",
                      borderRadius: "10px",
                      padding: "14px",
                    }}
                  >
                    <label
                      style={{
                        display: "block",
                        color: "var(--text-muted)",
                        fontSize: "14px",
                        marginBottom: "6px",
                      }}
                    >
                      Chip Load
                    </label>
                    <span
                      style={{
                        color: "var(--text-primary)",
                        fontWeight: 700,
                        fontSize: "18px",
                      }}
                    >
                      {formatNumber(results.chipLoad, 5)} IPT
                    </span>
                  </div>

                  <div
                    className="result-item"
                    style={{
                      background: "var(--bg-surface)",
                      border: "1px solid var(--border-color)",
                      borderRadius: "10px",
                      padding: "14px",
                    }}
                  >
                    <label
                      style={{
                        display: "block",
                        color: "var(--text-muted)",
                        fontSize: "14px",
                        marginBottom: "6px",
                      }}
                    >
                      Tool Life Minutes
                    </label>
                    <span
                      style={{
                        color: "var(--text-primary)",
                        fontWeight: 700,
                        fontSize: "18px",
                      }}
                    >
                      {formatNumber(results.lifeInMinutes, 2)} min
                    </span>
                  </div>

                  <div
                    className="result-item"
                    style={{
                      background: "var(--bg-surface)",
                      border: "1px solid var(--border-color)",
                      borderRadius: "10px",
                      padding: "14px",
                    }}
                  >
                    <label
                      style={{
                        display: "block",
                        color: "var(--text-muted)",
                        fontSize: "14px",
                        marginBottom: "6px",
                      }}
                    >
                      Tool Life Length
                    </label>
                    <span
                      style={{
                        color: "var(--text-primary)",
                        fontWeight: 700,
                        fontSize: "18px",
                      }}
                    >
                      {formatNumber(results.lifeInInches, 2)} in
                    </span>
                  </div>

                  <div
                    className="result-item"
                    style={{
                      background: "var(--bg-surface)",
                      border: "1px solid var(--border-color)",
                      borderRadius: "10px",
                      padding: "14px",
                    }}
                  >
                    <label
                      style={{
                        display: "block",
                        color: "var(--text-muted)",
                        fontSize: "14px",
                        marginBottom: "6px",
                      }}
                    >
                      Efficiency
                    </label>
                    <span
                      style={{
                        color: "var(--text-primary)",
                        fontWeight: 700,
                        fontSize: "18px",
                      }}
                    >
                      {results.efficiencyRating}
                    </span>
                  </div>

                  {results.estimatedToolCost !== null && (
                    <div
                      className="result-item"
                      style={{
                        background: "var(--bg-surface)",
                        border: "1px solid var(--border-color)",
                        borderRadius: "10px",
                        padding: "14px",
                      }}
                    >
                      <label
                        style={{
                          display: "block",
                          color: "var(--text-muted)",
                          fontSize: "14px",
                          marginBottom: "6px",
                        }}
                      >
                        Estimated Tool Cost
                      </label>
                      <span
                        style={{
                          color: "var(--text-primary)",
                          fontWeight: 700,
                          fontSize: "18px",
                        }}
                      >
                        {formatMoney(results.estimatedToolCost)}
                      </span>
                    </div>
                  )}
                </div>

                {results.percentOfToolLifeUsed !== null && (
                  <div
                    className="warnings"
                    style={{
                      background: "rgba(37, 99, 235, 0.10)",
                      border: "1px solid rgba(37, 99, 235, 0.28)",
                      borderRadius: "10px",
                      padding: "16px",
                      marginBottom: "20px",
                      color: "var(--text-secondary)",
                    }}
                  >
                    <h4
                      style={{
                        color: "var(--text-primary)",
                        marginBottom: "10px",
                      }}
                    >
                      Operation Estimate
                    </h4>
                    <p style={{ margin: 0 }}>
                      Estimated tool life used:{" "}
                      <strong>{formatNumber(results.percentOfToolLifeUsed, 1)}%</strong>
                    </p>
                  </div>
                )}

                {results.warnings.length > 0 && (
                  <div
                    className="warnings"
                    style={{
                      background: "rgba(245, 158, 11, 0.12)",
                      border: "1px solid rgba(245, 158, 11, 0.35)",
                      borderRadius: "10px",
                      padding: "16px",
                      marginBottom: "20px",
                    }}
                  >
                    <h4
                      style={{
                        color: "var(--text-primary)",
                        marginBottom: "10px",
                      }}
                    >
                      ⚠️ Warnings
                    </h4>
                    <ul style={{ paddingLeft: "20px", color: "var(--text-secondary)" }}>
                      {results.warnings.map((warning, index) => (
                        <li key={index}>{warning}</li>
                      ))}
                    </ul>
                  </div>
                )}

                <div
                  className="results-actions"
                  style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}
                >
                  <button
                    onClick={handleSave}
                    className="btn-primary"
                    type="button"
                    disabled={savingCalculation}
                  >
                    {savingCalculation ? "Saving..." : "💾 Save Calculation"}
                  </button>
                  <button
                    onClick={duplicateCalculation}
                    className="btn-secondary"
                    type="button"
                  >
                    📋 Duplicate
                  </button>
                  <button
                    onClick={copyToClipboard}
                    className="btn-secondary"
                    type="button"
                  >
                    {copied ? "✅ Copied" : "📄 Copy"}
                  </button>
                  <button
                    onClick={exportToCSV}
                    className="btn-secondary"
                    type="button"
                  >
                    📊 Export CSV
                  </button>
                </div>
              </>
            ) : (
              <div style={{ color: "var(--text-secondary)" }}>
                Enter valid machining and tooling values to calculate.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ToolCostPerInchCalculator;
